import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import Modal from '../../../../../../../components/v2/Modal';
import InputLabel from '../../../../../../../components/v2/InputLabel';
import TextField from '../../../../../../../components/v2/TextField';
import { CoachingActivity } from '../../../../../../../utils/log';
import useLogger from '../../../../../../../hooks/useLogger';
import LoadingOverlay from '../../../../../../components/LoadingOverlay';
import FirebaseContext from '../../../../../../../context/FirebaseContext';
import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import useSessionStore from '../../../../../../../hooks/useSessionStore';
import useToast from '../../../../../../hooks/useToast';
import useCanCreateChallengeProduct from '../../../../../../hooks/useCanCreateChallengeProduct';
import Form from '../../../../../../Model/Form';
import { Features, CHALLENGE_SUPPORTED_FEATURES } from '../../../../../../utils/product';
import {
  isAIFriendEnabled as isAIFriendEnabledOnPosthog,
  isAIFriendCreatePermissionEnabled,
  useHyroxEnabled,
} from '../../../../../../../utils/postHog';
import { ProductType } from '../../../../../../../Model/Product';
import envConfig from '../../../../../../../config';
import { PPFSelection, getPPFFieldsForSelection } from '../../postPaymentFormSelection';
import PostPaymentFormSelect from '../PostPaymentFormSelect';

import CommunitySelect from './CommunitySelect';
import {
  FeatureContainer,
  StyledButton,
  StyledRadioButtonGroup,
  StyledAddIcon,
} from './styles';
import texts from './texts.json';

// 'Challenge' is a creation-flow selector option, not a persisted product `type`.
// Challenge products are distinguished by `isChallengeProduct: true` (set
// server-side by the dedicated endpoint); their `type` stays STANDARD. Selecting
// this value routes creation to the challenge endpoint.
const CHALLENGE_PRODUCT_OPTION = 'CHALLENGE';

const AddProductModal = ({
  open,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isVideoFeedBackEnabled, setIsVideoFeedBackEnabled] = useState(true);
  const [isChatEnabled, setIsChatEnabled] = useState(true);
  const [isCheckInFeedBackEnabled, setIsCheckInFeedBackEnabled] = useState(true);
  const [isMealPlanEnabled, setIsMealPlanEnabled] = useState(false);
  const [isAiFriendEnabled, setIsAiFriendEnabled] = useState(false);
  const [productType, setProductType] = useState(ProductType.STANDARD);
  const [isSaving, setIsSaving] = useState(false);
  const [ppfForms, setPpfForms] = useState([]);
  const [ppfFormsLoaded, setPpfFormsLoaded] = useState(false);
  // One control for the whole post payment form decision: the coach's default or a
  // specific form. See postPaymentFormSelection for how it maps to the product fields.
  const [ppfSelection, setPpfSelection] = useState(PPFSelection.COACH_DEFAULT);
  const [selectedCommunityId, setSelectedCommunityId] = useState('');

  const { externalCoachDoc, coachDoc, products } = useContext(ExternalCoachContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const { isAdmin } = useSessionStore();

  /**
   * Show AI Friend feature option if AI Friend is enabled on posthog
   * and either the user has create permission or is an admin
   */
  const showAiFriendFeature = isAIFriendEnabledOnPosthog() && (isAIFriendCreatePermissionEnabled() || isAdmin);

  // Hyrox is rolled out per coach — without the flag the Hyrox option is hidden.
  const showHyroxOption = useHyroxEnabled(externalCoachDoc?.id);

  // CAM-only for now; the PostHog flag opens it to additional users without a
  // code change. Shared with the legacy convert action so both stay in sync.
  const showChallengeOption = useCanCreateChallengeProduct();

  // The product type selector only appears when there's more than one type to
  // choose from. Without any flag, every product is created as STANDARD.
  const showProductTypeSelector = showHyroxOption || showChallengeOption;

  // Standard is always available; Hyrox and Challenge each appear only when their
  // own access check passes.
  const productTypeOptions = [
    { label: texts.productTypes[ProductType.STANDARD], value: ProductType.STANDARD, show: true },
    { label: texts.productTypes[ProductType.HYROX], value: ProductType.HYROX, show: showHyroxOption },
    { label: texts.productTypes.CHALLENGE, value: CHALLENGE_PRODUCT_OPTION, show: showChallengeOption },
  ].filter((option) => option.show);

  const isChallenge = productType === CHALLENGE_PRODUCT_OPTION;

  const coachDefaultFormId = coachDoc?.defaultPostPaymentFormId || '';

  // Load the coach's native PPFs for the form dropdown. PostPaymentFormSelect handles
  // ordering (default first, then alphabetically) itself.
  useEffect(() => {
    const loadForms = async () => {
      try {
        const forms = await Form.getPinnablePostPaymentForms(externalCoachDoc.id);
        setPpfForms(forms);
      } catch (error) {
        Sentry.captureException(error, { extra: { coachId: externalCoachDoc?.id } });
        showToast(texts.errors.loadForms, { error: true });
      } finally {
        setPpfFormsLoaded(true);
      }
    };
    if (open && externalCoachDoc?.id) {
      loadForms();
    }
  }, [open, externalCoachDoc, showToast]);

  const setAIFriendFeatures = useCallback((value) => {
    setIsAiFriendEnabled(value);
    if (value) {
      setIsMealPlanEnabled(true);
      setIsChatEnabled(true);
    }
  }, []);

  // Feature checkboxes, rendered from config so the challenge-locking rules live
  // in one place. When a challenge product is selected, every checkbox is disabled
  // and only the challenge-supported features (meal plan) show as checked.
  const featureCheckboxes = [
    {
      id: Features.WORKOUT_VIDEO_FEEDBACK,
      label: texts.workoutFeedback,
      tooltip: texts.tooltips.workoutFeedback,
      checked: isVideoFeedBackEnabled,
      onChange: setIsVideoFeedBackEnabled,
    },
    {
      id: Features.NUTRITION_PLAN,
      label: texts.mealPlan,
      tooltip: texts.tooltips.mealPlan,
      checked: isMealPlanEnabled,
      onChange: setIsMealPlanEnabled,
    },
    {
      id: Features.CHECK_IN_NOTIFICATION,
      label: texts.checkInFeedback,
      tooltip: texts.tooltips.checkInFeedback,
      checked: isCheckInFeedBackEnabled,
      onChange: setIsCheckInFeedBackEnabled,
    },
    {
      id: Features.CHAT,
      label: texts.chat,
      tooltip: texts.tooltips.chat,
      checked: isChatEnabled,
      onChange: setIsChatEnabled,
    },
    ...(showAiFriendFeature ? [{
      id: Features.AI_FRIEND,
      label: texts.aiFriend,
      tooltip: texts.tooltips.aiFriend,
      checked: isAiFriendEnabled,
      onChange: setAIFriendFeatures,
    }] : []),
  ];

  const onAddProduct = async () => {
    let errorMsg;
    if (!name) {
      errorMsg = texts.errors.noName;
    } else if (!description) {
      errorMsg = texts.errors.noDescription;
    } else if (!isChallenge) {
      // A challenge product's name isn't surfaced (each challenge's display name
      // comes from its contract) and a coach can have several, so the
      // name-collision check doesn't apply to challenge creation — skip it.
      const productExists = products.find((product) => product.name.toLowerCase() === name.toLowerCase());
      if (productExists) {
        errorMsg = texts.errors.productExists;
      }
    }
    if (errorMsg) {
      showToast(errorMsg, { error: true });
      return;
    }
    try {
      setIsSaving(true);
      // Challenge products go through their own endpoint, which forces the
      // supported feature set (meal plan only) server-side. Regular products use
      // products-createProduct with the features selected here.
      const enabledFeatures = featureCheckboxes
        .filter(({ checked }) => checked)
        .map(({ id }) => id);
      // The create endpoints only need the pin when there is one; an absent
      // postPaymentFormId means the product follows the coach's default PPF.
      const { postPaymentFormId } = getPPFFieldsForSelection(ppfSelection);
      const ppfFields = postPaymentFormId ? { postPaymentFormId } : {};
      const apiResponse = isChallenge
        ? await remote('createChallengeProduct', {
          coachId: externalCoachDoc?.id,
          challengeName: name,
          description,
          challengePostPaymentForm: envConfig.challenges.postPaymentForm,
          ...ppfFields,
          ...(selectedCommunityId ? { communityId: selectedCommunityId } : {}),
        })
        : await remote('products-createProduct', {
          coachId: externalCoachDoc?.id,
          name,
          description,
          type: productType,
          enabledFeatures,
          ...ppfFields,
          ...(selectedCommunityId ? { communityId: selectedCommunityId } : {}),
        });
      const { error } = await apiResponse.json();
      if (error) {
        showToast(error, { error: true });
        setIsSaving(false);
        return;
      }
      logCoachingActivity(CoachingActivity.CREATED_PRODUCT, {
        productType: isChallenge ? CHALLENGE_PRODUCT_OPTION : productType,
        features: isChallenge ? CHALLENGE_SUPPORTED_FEATURES : enabledFeatures,
      });
      showToast(texts.success, { success: true });
      setIsSaving(false);
      onClose();
    } catch (error) {
      setIsSaving(false);
      Sentry.captureException(error, { extra: { coachId: externalCoachDoc?.id, isChallenge } });
      showToast(error.message, { error: true });
    }
  };

  return (
    <Modal
      open={open}
      title={texts.title}
      onClose={onClose}
      actionButtons={(
        <StyledButton onClick={onAddProduct}>
          <StyledAddIcon />
          {texts.addProduct}
        </StyledButton>
      )}
    >
      <InputLabel>{texts.fieldNames.productName}</InputLabel>
      <TextField onChange={(e) => setName(e.target.value)} value={name} />
      <InputLabel>{texts.fieldNames.productDescription}</InputLabel>
      <TextField multiline minRows={3} onChange={(e) => setDescription(e.target.value)} value={description} />
      {showProductTypeSelector && (
        <>
          <InputLabel>{texts.fieldNames.productType}</InputLabel>
          <StyledRadioButtonGroup
            isRow
            options={productTypeOptions}
            showStyledOptions={false}
            selectedOption={productType}
            onOptionChange={setProductType}
          />
        </>
      )}
      <InputLabel>{texts.fieldNames.postPaymentForm}</InputLabel>
      <PostPaymentFormSelect
        forms={ppfForms}
        formsLoaded={ppfFormsLoaded}
        value={ppfSelection}
        onChange={setPpfSelection}
        defaultFormId={coachDefaultFormId}
      />
      <CommunitySelect
        coachId={externalCoachDoc?.id}
        value={selectedCommunityId}
        onChange={setSelectedCommunityId}
      />
      <InputLabel>{texts.fieldNames.features}</InputLabel>
      <FeatureContainer>
        {featureCheckboxes.map(({
          id, label, tooltip, checked, onChange,
        }) => {
          const supportedForChallenge = CHALLENGE_SUPPORTED_FEATURES.includes(id);
          const displayChecked = isChallenge ? supportedForChallenge : checked;
          const tooltipTitle = isChallenge && !supportedForChallenge
            ? texts.tooltips.challengeUnsupported
            : tooltip;
          return (
            <Tooltip key={id} title={tooltipTitle} placement="right">
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={displayChecked}
                    disabled={isChallenge}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
                label={label}
              />
            </Tooltip>
          );
        })}
      </FeatureContainer>
      <LoadingOverlay isLoading={isSaving} />
    </Modal>
  );
};

AddProductModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default compose(observer)(AddProductModal);
